// Results — editorial + prestige treatment per Brand PDF page 9
// All case studies use the "prestige badge" pattern (warm ink bg, amber labels,
// amber accent on big numbers, amber vertical rule on the case-study tag).
// The editorial pullquote uses the "editorial moment" pattern (cream wash bg,
// Fraunces italic, 4px amber vertical rule on the left).

import React from "react";
import { useTranslation } from "react-i18next";
import Icon from "../components/icons.jsx";
import { Eyebrow, PhotoFrame } from "../components/ui.jsx";
import { useLocaleNav } from "../lib/useLocaleNav.js";
import { goToBooking } from "../lib/handoff.js";

const CASE_IMAGES = [
  "/assets/featured_la-ceaun-piata-sfatului.webp",
  "/assets/featured_tucano.webp",
  "/assets/featured_kifla.webp",
  "/assets/featured_ola.webp",
  "/assets/featured_hopaa.webp"
];

export default function Results() {
  const { t } = useTranslation();
  const go = useLocaleNav();
  const caseData = t("results.cases", { returnObjects: true });
  const cases = caseData.map((c, i) => ({ ...c, image: CASE_IMAGES[i] }));
  const caseStudyLabel = t("results.caseStudyLabel");

  return (
    <main>
      {/* HERO — no gradient, light bg */}
      <section style={{ background: "var(--bg)" }}>
        <div className="container" style={{ padding: "56px 24px 96px" }}>
          {/* Amber eyebrow with small horizontal rule (page 5 / page 9 convention) */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 12, fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 500, letterSpacing: ".22em", textTransform: "uppercase", color: "var(--marigold)" }}>
            <span style={{ width: 22, height: 1, background: "var(--marigold)" }} />
            {t("results.intro.eyebrow")}
          </div>
          <h1 className="tt-h tt-h-xl" style={{ marginTop: 24 }}>
            {t("results.intro.headline")}<br />
            <span style={{ fontFamily: "Fraunces, serif", fontStyle: "italic", fontWeight: 400 }}>{t("results.intro.headlineItalic")}</span>
          </h1>
        </div>
      </section>

      {/* CASES — alternating ink (prestige) and wash (editorial) per PDF page 9 */}
      {cases.map((c, i) => (<FeaturedCase key={c.name} c={c} variant={i % 2 === 0 ? "ink" : "wash"} caseStudyLabel={caseStudyLabel} />))}

      {/* EDITORIAL MOMENT — cream wash bg, Fraunces italic, amber vertical rule */}
      <section style={{ background: "var(--bg-2)", padding: "140px 0" }}>
        <div className="container">
          <div style={{ display: "flex", gap: 32 }}>
            <div style={{ width: 4, background: "var(--marigold)", borderRadius: 2, flexShrink: 0 }} />
            <div>
              <p style={{ margin: 0, fontFamily: "Fraunces, serif", fontStyle: "italic", fontWeight: 400, fontSize: "clamp(28px, 3.6vw, 48px)", lineHeight: 1.25, color: "var(--ink)", maxWidth: 880 }}>
                {t("results.editorial.quote")}
              </p>
              <div style={{ marginTop: 28, display: "flex", alignItems: "center", gap: 12, fontFamily: "Inter, sans-serif", fontSize: 12, letterSpacing: ".16em", textTransform: "uppercase", color: "var(--ink-2)", fontWeight: 500 }}>
                <span style={{ width: 22, height: 1, background: "var(--marigold)" }} />
                {t("results.editorial.author")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA — brand gradient */}
      <section className="bg-bold" style={{ padding: "140px 0" }}>
        <div className="container">
          <h2 className="tt-h tt-h-xl" style={{ color: "var(--ink)" }}>{t("results.finalCta")}</h2>
          <div style={{ marginTop: 40 }}>
            <a href="#" className="btn btn-lg" onClick={(e) => { e.preventDefault(); goToBooking(); }} style={{ background: "var(--ink)", color: "var(--bg)" }}>{t("common.bookDemo")} <Icon name="arrow-right" size={16} /></a>
          </div>
        </div>
      </section>
    </main>
  );
}

const CASE_VARIANTS = {
  ink: {
    bg: "var(--ink)",
    text: "var(--bg)",
    sub: "rgba(255,255,255,.72)",
    bullet: "rgba(255,255,255,.4)",
    big2: "rgba(255,255,255,.78)",
    quote: "rgba(255,255,255,.94)",
    muted: "rgba(255,255,255,.55)",
    divider: "rgba(255,255,255,.14)",
    metricVal: "var(--bg)",
    metricLabel: "rgba(255,255,255,.7)",
    authorRule: "rgba(255,255,255,.4)",
    photoTone: "wash"
  },
  wash: {
    bg: "var(--bg-2)",
    text: "var(--ink)",
    sub: "var(--ink-2)",
    bullet: "var(--muted)",
    big2: "var(--ink-2)",
    quote: "var(--ink)",
    muted: "var(--muted)",
    divider: "var(--line)",
    metricVal: "var(--ink)",
    metricLabel: "var(--ink-2)",
    authorRule: "var(--line-2)",
    photoTone: "ink"
  }
};

function FeaturedCase({ c, variant = "ink", caseStudyLabel = "Case study" }) {
  const v = CASE_VARIANTS[variant] || CASE_VARIANTS.ink;
  return (
    <section style={{ background: v.bg, color: v.text, padding: c.quote ? "120px 0" : "88px 0" }}>
      <div className="container">
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.1fr)", gap: 64, alignItems: "stretch" }} className="featured-grid">
          <div>
            <PhotoFrame ratio={c.quote ? "4/5" : "6/5"} tone={v.photoTone} label={c.name} caption={c.photo} hint={c.photoHint} src={c.image} alt={c.name} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", paddingTop: 4 }}>
            <div>
              {/* Prestige badge: amber label + amber vertical rule (page 9) — works on both ink and wash */}
              <div style={{ display: "flex", gap: 14, alignItems: "stretch" }}>
                <span style={{ width: 4, background: "var(--marigold)", borderRadius: 2, flexShrink: 0 }} />
                <div>
                  <div style={{ fontFamily: "Inter, sans-serif", fontSize: 11.5, fontWeight: 600, letterSpacing: ".22em", textTransform: "uppercase", color: "var(--marigold)" }}>
                    {caseStudyLabel}
                  </div>
                  <h2 className="tt-h tt-h-md" style={{ margin: "8px 0 0", color: v.text }}>{c.name}</h2>
                  <div style={{ marginTop: 10, fontFamily: "Inter, sans-serif", fontSize: 14, color: v.sub }}>
                    {[c.city, ...c.segment.split(" · ")].map((part, idx, arr) => (
                      <React.Fragment key={idx}>
                        {part}
                        {idx < arr.length - 1 && <span style={{ color: v.bullet, margin: "0 8px" }}>·</span>}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 48, paddingBottom: c.quote ? 28 : 0, borderBottom: c.quote ? `1px solid ${v.divider}` : "none" }}>
                <div className="tt-big" style={{ fontSize: "clamp(80px, 10vw, 132px)", color: "var(--marigold)", lineHeight: 0.9, letterSpacing: "-0.04em", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: ".08em" }}>
                  {c.big.includes("→") ? (() => {
                    const [from, to] = c.big.split("→").map(s => s.trim());
                    return (
                      <>
                        <span>{from}</span>
                        <span style={{ fontSize: ".48em", opacity: .8, fontWeight: 700 }}>→</span>
                        <span>{to}</span>
                      </>
                    );
                  })() : c.big}
                </div>
                <div style={{ marginTop: 10, fontFamily: "Satoshi, sans-serif", fontWeight: 600, fontSize: 15, color: v.big2, letterSpacing: ".02em" }}>{c.big2}</div>
              </div>
              {c.quote && (
                <blockquote style={{ margin: "40px 0 0", padding: 0, display: "flex", gap: 20 }}>
                  <span style={{ width: 4, background: "var(--marigold)", borderRadius: 2, flexShrink: 0 }} />
                  <div>
                    <p style={{ margin: 0, fontFamily: "Fraunces, serif", fontStyle: "italic", fontWeight: 400, fontSize: "clamp(19px, 1.7vw, 23px)", lineHeight: 1.4, color: v.quote, maxWidth: 520 }}>
                      &ldquo;{c.quote}&rdquo;
                    </p>
                    {c.author && (
                      <footer style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12, fontFamily: "Inter, sans-serif", fontSize: 12, letterSpacing: ".16em", textTransform: "uppercase", color: v.muted, fontWeight: 500 }}>
                        <span style={{ width: 18, height: 1, background: v.authorRule }} />
                        {c.author}
                      </footer>
                    )}
                  </div>
                </blockquote>
              )}
            </div>
            <div style={{ marginTop: 48, paddingTop: 28, borderTop: `1px solid ${v.divider}`, display: "grid", gridTemplateColumns: `repeat(${Math.max(c.metrics.length - 1, 1)}, minmax(0, 1fr))`, gap: 24 }}>
              {c.metrics.slice(1).map(m => (
                <div key={m.label}>
                  <div style={{ fontFamily: "Satoshi, sans-serif", fontWeight: 700, fontSize: "clamp(26px, 2.6vw, 34px)", letterSpacing: "-0.025em", color: v.metricVal }}>{m.value}</div>
                  <div style={{ marginTop: 6, fontSize: 12.5, color: v.metricLabel, lineHeight: 1.4 }}>{m.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <style>{`@media (max-width: 900px){.featured-grid{grid-template-columns:1fr !important; gap:32px !important}}`}</style>
    </section>
  );
}
